import {
    ButtonGroup,
    Button,
    Modal,
    Spinner,
    TextField,
    useSnackbar,
    TextArea,
  } from "@livingdesign/react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { RESTORE_REASONS, SELECT_ADJUSTMENT_REASON, SELECT_RESTORE_REASON } from "../../../constants/constants";
import { CANCEL, RESTORE, SUBMIT } from "../../../constants/actionButtons";
import MasterDataAutocomplete from "../../Autocomplete/MasterDataAutocomplete/MasterDataAutocomplete";
import LoadComponent from "../../LoadComponent/LoadComponent";
import { getErrorMessage, onInputNumberKeyDown } from "../../../helpers/utils";
import { Dollar } from "@livingdesign/icons";
import { LineItemBulkUpdateStyles } from "./LineItemBulkUpdate.styles";
import { filterPayload } from "../../../helpers/commonUtils";
import { postServiceWithHeaders } from "../../../axios/axios";
import { INVOICE_SERVICE } from "../../../constants/baseURLs";
import { BULK_UPDATE_LINE_ITEM_SUCCESS_MESSAGE } from "../../../constants/messages";
import Label from "../../TextField/Label/Label";
import { setAdjustedSelectLineItems, setNonAdjustedSelectLineItems } from "../../../store/actions/invoiceActions";

const LineItemBulkUpdate = (props) => {
  const {
    fetchInvoiceDetail,
    updateProperty,
    openBulkUpdateModal,
    setOpenBulkUpdateModal,
    selectedLineItems,
    selectedLineItemsNumberMap,
    loading,
    invoiceId,
    invoiceDetail
  } = props;
  const loadComponentProps = {
    loader: loading,
    skeletonHeight: '18px',
    skeletonVariant: 'reactangle',
    skeletonWidth: '100%',
    skeletonLines: 1
  };
  const [rate, setRate] = useState();
  const [units, setUnits] = useState();
  const classes = LineItemBulkUpdateStyles();
  const [lineItemAdjustmentReason, setLineItemAdjustmentReason] = useState();
  const [lineItemAdjustmentComment, setLineItemAdjustmentComment] = useState()
  const [restoreReason, setRestoreReason] = useState();
  const [restoreComment, setRestoreComment] = useState();
  const selectedItemsNumber = selectedLineItems.length;
  const [updatedHeader, setUpdatedHeader] = useState()
  const [payloadValue, setPayloadValue] = useState({});  
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [jobId, setJobId] = useState();
  const [isRestore, setIsRestore] = useState(false);
  const { addSnack } = useSnackbar();
  const dispatch = useDispatch();


  const TITLE = {
    "NET_AMOUNT_TO_ZERO": `Set net amount to zero for ${selectedItemsNumber} line item(s)`,
    "UPDATE_RATE": `Update rate for ${selectedItemsNumber} line item(s)`,
    "UPDATE_UNITS": `Update units for ${selectedItemsNumber} line item(s)`,
    "RESTORE_PREVIOUS": `Please select a reason to restore ${selectedItemsNumber} line item(s) to their previous state`,
    "RESTORE_ORIGINAL": `Please select a reason to restore ${selectedItemsNumber} line item(s) to their original state`,
  }

  const LINE_ITEM_OPERATION_TYPE = {
    "NET_AMOUNT_TO_ZERO": "SET_NET_AMOUNT_TO_ZERO",
    "UPDATE_RATE": "UPDATE_RATE",
    "UPDATE_UNITS": "UPDATE_UNITS",
    "RESTORE_PREVIOUS": "RESTORE_TO_PREVIOUS_VERSION",
    "RESTORE_ORIGINAL":"RESTORE_TO_ORIGINAL_VERSION",
  }

  useEffect(()=>{
    resetValues()
  },[openBulkUpdateModal, updateProperty])

  const resetValues = () => {
    setLineItemAdjustmentReason("");
    setLineItemAdjustmentComment("");
    setRate();
    setUnits();
    setJobId();
    setRestoreReason(RESTORE_REASONS[0]);
    setRestoreComment();
    setIsRestore(false);
    if(updateProperty){
      if (["RESTORE_PREVIOUS", "RESTORE_ORIGINAL"].indexOf(updateProperty)>=0) {
        if (updateProperty === "RESTORE_ORIGINAL") {
          setPayloadValue({"restoreReason": RESTORE_REASONS[0], "restoreComment": "", restoreToOriginalVersion: true });
        } else {
          setPayloadValue({"restoreReason": RESTORE_REASONS[0], "restoreComment": "", restoreToPreviousVersion: true });
        }
        setIsRestore(true)
      } else if (updateProperty === "NET_AMOUNT_TO_ZERO") {
        setPayloadValue({"shouldZeroNetAmount": true });
      }
      else {
        setPayloadValue({})
      }
    }
  }

  const onSubmitClick = () => {
    setBulkActionLoading(true)
    let lineItemObj = {}
    setUpdatedHeader(`Updated ${selectedItemsNumber} line items`)
    selectedLineItems.map((item)=>{
      lineItemObj[item] = payloadValue
    })
    lineItemObj = filterPayload(lineItemObj)
    const data = {
      "request": {
        "invoiceId": invoiceId,
        "isInvoiceLevelOperation":false, 
        "lineItemsAdjustmentRequestsMap":  lineItemObj,
        "lineItemOperationType": LINE_ITEM_OPERATION_TYPE[updateProperty],
        "organizationInvoiceNumber": invoiceDetail?.organizationInvoiceNumber,
        "matterNumber": invoiceDetail?.matterNumber,
        "organizationName": invoiceDetail?.organizationName
      }
    }
    const body = {
      command:  "BULK_SUBLINE_ITEM_CREATE",
      identifiers: selectedLineItems.join(","),
      validationType : "SYNC",
      executionType: "ASYNC",
      dataBag: JSON.stringify(data)
    }
    const headers = {
      'Content-Type': `multipart/form-data`,
    };

    const filteredBody = filterPayload(body)
    postServiceWithHeaders(
      INVOICE_SERVICE,
      `/jobs/v1/submit`,
      filteredBody,
      headers
    )
    .then((response) => {
      const res = JSON.parse(response?.data)
      setJobId(res?.jobId);
      setBulkActionLoading(false)
    })
    .catch((error) => {
      setOpenBulkUpdateModal(false)
      addSnack({
        message: getErrorMessage(error)
      });
      setBulkActionLoading(false)
    })
    .finally(()=>{
      dispatch(setAdjustedSelectLineItems({ids: [], idNumberMap: []}))
      dispatch(setNonAdjustedSelectLineItems({ids: [], idNumberMap: []}));
    })
    ;
  }

  const handleChange = (propertyName, value) => {
    switch(propertyName) {
      case "rate":
        setRate(value)
        setPayloadValue((prev)=>({...prev,"targetRate": value ?? parseFloat(value).toFixed(2)}))
        break;
      case "units":
        setUnits(value)
        setPayloadValue((prev)=>({...prev,"targetUnit": value ?? parseFloat(value).toFixed(2)}))
        break;
      case "lineItemAdjustmentReason":
        setLineItemAdjustmentReason(value)
        if (!value){
          let payload = {...payloadValue}
          delete payload.reason
          setPayloadValue(payload);
        }
        else{
          setPayloadValue((prev)=>({...prev,"reason": value}))
        }
        break;
      case "lineItemAdjustmentComment":
        setLineItemAdjustmentComment(value)
        setPayloadValue((prev)=>({...prev,"comment": value}))
        break;
      case "restoreReason":
        setRestoreReason(value)
        if (!value){
          let payload = {...payloadValue}
          delete payload.restoreReason
          setPayloadValue(payload);
        }
        else{
          setPayloadValue((prev)=>({...prev,"restoreReason": value}))
        }
        break;
      case "restoreComment":
        setRestoreComment(value)
        setPayloadValue((prev)=>({...prev,"restoreComment": value}))
        break;
      default:
        break;
    }
  }

  const onRestoreClick = () => {
    setBulkActionLoading(true)
    setUpdatedHeader(`Restored ${selectedItemsNumber} line items`)
    let lineItemNumberMap = {}

    lineItemNumberMap = selectedLineItemsNumberMap?.reduce((acc, curr)=>({...acc, ...curr}),{})

    const dataBag = {  
      "invoiceId": invoiceId,
      "restoreRequest":  filterPayload(payloadValue),
      "lineItemNumberMap": lineItemNumberMap,
      "lineItemOperationType": LINE_ITEM_OPERATION_TYPE[updateProperty],
      "organizationInvoiceNumber": invoiceDetail?.organizationInvoiceNumber,
      "matterNumber": invoiceDetail?.matterNumber,
      "organizationName": invoiceDetail?.organizationName
    }
    const body = {
      command:  "BULK_LINEITEM_RESTORE",
      identifiers: selectedLineItems.join(","),
      validationType : "SYNC",
      executionType: "ASYNC",
      dataBag: JSON.stringify(dataBag)
    }
    const headers = {
      'Content-Type': `multipart/form-data`,
    };

    const filteredBody = filterPayload(body)
    postServiceWithHeaders(
      INVOICE_SERVICE,
      `/jobs/v1/submit`,
      filteredBody,
      headers
    )
    .then((response) => {
      const res = JSON.parse(response?.data)
      setJobId(res?.jobId);
      setBulkActionLoading(false)
    })
    .catch((error) => {
      setOpenBulkUpdateModal(false)
      addSnack({
        message: getErrorMessage(error)
      });
      setBulkActionLoading(false)
    })
    .finally(()=>{
      dispatch(setAdjustedSelectLineItems({ids: [], idNumberMap: []}))
      dispatch(setNonAdjustedSelectLineItems({ids: [], idNumberMap: []}));
    })
    ;
  }

  const ModalActions = () => {
    return (
      <ButtonGroup>
        <Button
          data-testid="cancel-btn"
          id="cancel-btn"
          size="medium"
          onClick={() => setOpenBulkUpdateModal(false)}
          disabled={loading}
        >
          {CANCEL}
        </Button>
        {isRestore ?
          <Button
            variant="primary"
            size="medium"
            data-testid="restore-btn"
            id="restore-btn"
            onClick={onRestoreClick}
            disabled={loading || !payloadValue.restoreReason}
            >
            {loading ? (
            <Spinner color="white" size="small" />
            ) : (
            <>
            {RESTORE} {bulkActionLoading && <Spinner color="white" size="small" />}
            </>
            )}
          </Button> :
        <Button
          variant="primary"
          size="medium"
          data-testid="save-btn"
          id="save-btn"
          onClick={onSubmitClick}
          disabled={loading || !lineItemAdjustmentReason || (updateProperty=="UPDATE_UNITS" && !payloadValue?.targetUnit) || (updateProperty=="UPDATE_RATE" && !payloadValue?.targetRate)}
        >
          {loading ? (
            <Spinner color="white" size="small" />
          ) : (
            <>
            {SUBMIT} {bulkActionLoading && <Spinner color="white" size="small" />}
            </>
          )}
        </Button>}
      </ButtonGroup>
    );
  };

  const handleCloseModal = () => {
    setOpenBulkUpdateModal(false);
    resetValues();
    jobId && fetchInvoiceDetail();
  }

  return (
    <>
    <Modal
      data-testid="bulk-update-modal"
      actions={!jobId && <ModalActions/>}
      isOpen={openBulkUpdateModal}
      onClose={handleCloseModal}
      size="medium"
      title={!jobId ? TITLE[updateProperty]: updatedHeader}
    >
      {jobId ?
        <p id="success-message" data-testid="success-message">{BULK_UPDATE_LINE_ITEM_SUCCESS_MESSAGE}</p>:
        <>
          <div className={classes.fieldStyle}>
            {updateProperty==="UPDATE_RATE" && 
              <>
                <Label text={"Rate"} textWithColon={false} mandatoryField/>
                <TextField
                  label={false}
                  type="number"
                  value={rate}
                  onKeyDown={(e) => onInputNumberKeyDown(e)}
                  onChange={(v) => handleChange("rate", v.target.value)}
                  leadingIcon={<><Dollar size="medium" /></>}
                  data-testid="rate-value"
                  id="rate-value"
                  textFieldProps={{
                    min:0.1
                  }}
                  onWheel={(e) => { e.target.blur() }}
                  required={true}
                />
              </>
            }

            {updateProperty==="UPDATE_UNITS" && 
              <>
                <Label text={"Units"} textWithColon={false} mandatoryField />
                <TextField
                  label={false}
                  type="number"
                  value={units}
                  onKeyDown={(e) => onInputNumberKeyDown(e)}
                  onChange={(v) => handleChange("units", v.target.value)}
                  data-testid="units-value"
                  id="units-value"
                  textFieldProps={{
                    min:0.1
                  }}
                  onWheel={(e) => { e.target.blur() }}
                /> 
              </>
            }
          </div>
          {isRestore ?
            <div className={classes.fieldStyle}>
              <Label text={"Restore Reason"} textWithColon={false} mandatoryField />
              {/* <MasterDataAutocomplete
                value={restoreReason}
                setValue={(value)=>handleChange("restoreReason", value?.value)}
                dataType={"invoiceRestoreReasonCode"}
                required={true}
                testid="restore-reason"
                placeholder={SELECT_RESTORE_REASON}
              /> */}
              <MasterDataAutocomplete
                value={restoreReason}
                setValue={(value)=>handleChange("restoreReason", value?.value)}
                dataType={"invoiceRestoreReasonCode"}
                testid="restore-reason"
                placeholderValue={SELECT_RESTORE_REASON}
              />
            </div> :
            <div className={classes.fieldStyle}>
              <Label text={"Adjustment Reason"} textWithColon={false} mandatoryField />
              <LoadComponent {...loadComponentProps}>
              <MasterDataAutocomplete
                value={lineItemAdjustmentReason}
                setValue={(value)=>handleChange("lineItemAdjustmentReason", value?.value)}
                dataType={"invoiceAdjustmentReason"}
                testid="adjustments-reason"
                placeholderValue={SELECT_ADJUSTMENT_REASON}
              />
            </LoadComponent>
          </div>
          }
          <div className={classes.fieldStyle}>
            <Label text={"Comments"} textWithColon={false} />
            <LoadComponent {...loadComponentProps}>
            <TextArea
              value={isRestore? restoreComment : lineItemAdjustmentComment}
              onChange={(e) => handleChange(isRestore ? "restoreComment" : "lineItemAdjustmentComment", e.target.value)}
              label={false}
              maxLength={'250'}
              type="text"
              data-testid="comment"
            />
            </LoadComponent>
          </div>
        </>
      }
  </Modal>
  </>
  );
};

export default LineItemBulkUpdate;